import { TenantContactDetails } from "../tenant/TenantContactDetails";
import { useSiteConfig } from "../tenant/SiteConfigContext";
import { createContext, type ComponentChildren } from "preact";
import { useContext } from "preact/hooks";
import type { PortalAccount } from "../../store/portalAuthApi";
import { ErrorPanel, LoadingPanel } from "../../components/StatePanels";
import { AccountOptions } from "./AccountSwitcher";
import type { PortalAccountsViewState } from "./portalAccountsView";

const PortalAccountContext = createContext<PortalAccount | null>(null);

function PortalAccountProvider({
  account,
  children,
}: {
  account: PortalAccount;
  children: ComponentChildren;
}) {
  return <PortalAccountContext.Provider value={account}>{children}</PortalAccountContext.Provider>;
}

export function usePortalAccount(): PortalAccount {
  const account = useContext(PortalAccountContext);
  if (!account) throw new Error("Portal account is not available in this page context.");
  return account;
}

type Props = {
  viewState: PortalAccountsViewState;
  busy: boolean;
  onRetry: () => void;
  onSelect: (customerId: string) => Promise<void>;
  children: ComponentChildren;
};

export function PortalAccounts({ viewState, busy, onRetry, onSelect, children }: Props) {
  const { displayName } = useSiteConfig();
  let content: ComponentChildren;
  switch (viewState.status) {
    case "error":
      return (
        <ErrorPanel
          title="Unable to load your accounts"
          message={viewState.message}
          onRetry={onRetry}
        />
      );
    case "switching":
      content = <LoadingPanel title="Switching accounts" />;
      break;
    case "loading":
      content = <LoadingPanel title="Loading your accounts" />;
      break;
    case "empty":
      content = (
        <section class="empty-state">
          <h2>No linked accounts</h2>
          <p>Contact {displayName} to confirm the email address on your account.</p>
          <TenantContactDetails className="portal-account-contact" />
        </section>
      );
      break;
    case "chooseAccount":
      content = (
        <section class="portal-account-picker">
          <h2>Choose an account</h2>
          <p class="muted">Select the account you’d like to view.</p>
          <AccountOptions
            accounts={viewState.accounts}
            currentCustomerId={null}
            onSelect={onSelect}
          />
        </section>
      );
      break;
    case "ready":
      content = (
        <section class="portal-account-body" aria-label="Account portal">
          <PortalAccountProvider account={viewState.account}>{children}</PortalAccountProvider>
        </section>
      );
      break;
  }

  return (
    <div class="portal-accounts">
      <div aria-busy={busy}>{content}</div>
    </div>
  );
}
